// Records where a new user came from: the first tagged touch (utm_source,
// utm_medium, utm_campaign) a browser or device saw, kept in localStorage until
// the first-sign-in seed in authUtils.js copies it into users/{uid}/acquisition.
//
// This module deliberately imports neither Vue nor Firebase, so it can be unit
// tested in isolation and so main.js can run it before anything else boots.
//
// The normalization here IS the RTDB rule on users/$uid/acquisition, applied on
// the client first. The seed is one all-or-nothing transaction, so a value the
// rule refuses would cost the new account its entire profile, not just the
// acquisition record. tests/unit/acquisitionAllowlistSync.test.js pins the
// constants below against database.rules.json for exactly that reason.

import { STORAGE_KEYS } from "@/constants/storageKeys.js";

// The longest token the rule accepts (`newData.val().length <= 40`).
export const ACQUISITION_TOKEN_MAX_LENGTH = 40;

// The character class every stored token must match. It is narrower than what
// UTM values usually look like on purpose: no `.` (illegal in an RTDB key, and
// the source becomes a key in the daily metrics snapshot), no `_` (Slack's
// italic delimiter, and the prefix the backend reserves for its own counter
// keys), and nothing that is markup anywhere the value is rendered.
export const ACQUISITION_TOKEN_PATTERN = /^[a-z0-9-]+$/;

// 2020-01-01T00:00:00Z in epoch milliseconds. The rule refuses a landedAt
// below this, so the client drops such a value before the seed.
export const ACQUISITION_LANDED_AT_FLOOR_MS = 1577836800000;

// A stored first touch is usable for 90 days from the moment this device
// stored it, matching the retention Google documents for the Play install
// referrer so the web and Android arms age out attribution the same way.
export const ACQUISITION_WINDOW_MS = 90 * 24 * 60 * 60 * 1000;

// Trim, lower-case, turn every `.` and `_` into `-`, cut to 40 characters, and
// then require the character class. Returns null for anything that still fails.
function normalizeToken(value) {
  if (typeof value !== "string") return null;
  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/[._]/g, "-")
    .slice(0, ACQUISITION_TOKEN_MAX_LENGTH);
  return ACQUISITION_TOKEN_PATTERN.test(normalized) ? normalized : null;
}

// True only for a value the rule's TOKEN expression accepts as-is.
function isValidToken(value) {
  return (
    typeof value === "string" &&
    value.length >= 1 &&
    value.length <= ACQUISITION_TOKEN_MAX_LENGTH &&
    ACQUISITION_TOKEN_PATTERN.test(value)
  );
}

function isValidLandedAt(value) {
  return Number.isFinite(value) && value >= ACQUISITION_LANDED_AT_FLOOR_MS;
}

/**
 * Turn a query string (web) or a decoded Play referrer string (Android, same
 * `key=value&key=value` shape) into a normalized record, or null when there is
 * no valid utm_source.
 *
 * `landedAt` is `now` unless `options.allowForwardedLanding` is set and the
 * string carries a numeric `al_landed`, which is how the web-to-Play badge
 * forwards the original web landing time through a Play install. The web path
 * never sets that option, so a visitor cannot choose their own timestamp by
 * editing the landing URL. Pass a non-number `now` to leave landedAt unset for
 * a caller that picks its own fallback time.
 */
export function parseUtmParams(queryString, now, options = {}) {
  if (typeof queryString !== "string" || queryString === "") return null;
  let params;
  try {
    params = new URLSearchParams(queryString);
  } catch {
    return null;
  }
  const source = normalizeToken(params.get("utm_source"));
  if (!source) return null;

  const record = { source };
  const medium = normalizeToken(params.get("utm_medium"));
  if (medium) record.medium = medium;
  const campaign = normalizeToken(params.get("utm_campaign"));
  if (campaign) record.campaign = campaign;

  const forwarded = options.allowForwardedLanding ? params.get("al_landed") : null;
  if (forwarded !== null && /^\d+$/.test(forwarded)) {
    record.landedAt = Number(forwarded);
  } else if (Number.isFinite(now)) {
    record.landedAt = now;
  }
  return record;
}

// The stored record, parsed, or null when there is none or storage fails.
function readStored() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEYS.ACQUISITION);
    if (raw === null) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed
      : null;
  } catch {
    return null;
  }
}

// A stored record counts only with a numeric storedAt inside the window. One
// without storedAt (hand-edited, or partially written) is treated as absent,
// and so is one stored in the future: a device clock corrected backward would
// otherwise give it a negative age that looks fresh until the clock caught up.
function isFresh(stored, now) {
  if (stored === null || !Number.isFinite(stored.storedAt)) return false;
  const age = now - stored.storedAt;
  return age >= 0 && age < ACQUISITION_WINDOW_MS;
}

/**
 * Store `record` as this device's first touch, stamped with `storedAt: now`,
 * unless a fresh first touch is already stored. Never throws: storage can be
 * unavailable (private browsing) and must never break boot.
 */
export function rememberFirstTouch(record, now) {
  if (!record) return;
  if (isFresh(readStored(), now)) return;
  try {
    window.localStorage.setItem(
      STORAGE_KEYS.ACQUISITION,
      JSON.stringify({ ...record, storedAt: now })
    );
  } catch {
    // Storage unavailable. Attribution is lost for this visit, nothing else.
  }
}

/**
 * The record the first-sign-in seed should write, or null. Synchronous on
 * purpose: the seed must issue its transaction in the same tick as the
 * sign-in completion that calls it, before anything else is awaited (see
 * seedUserProfileIfMissing in authUtils.js).
 *
 * Every leaf is re-checked against the rule before it is returned, because
 * localStorage can hold anything. An invalid source drops the whole record; an
 * invalid optional leaf is omitted on its own. landedAt gets only the 2020
 * floor the rule applies, and deliberately no bound relative to this device's
 * clock, because the rule has none either.
 */
export function readAcquisitionForSeed(now) {
  const stored = readStored();
  if (!isFresh(stored, now)) return null;
  if (!isValidToken(stored.source)) return null;

  const record = { source: stored.source };
  if (isValidToken(stored.medium)) record.medium = stored.medium;
  if (isValidToken(stored.campaign)) record.campaign = stored.campaign;
  if (isValidLandedAt(stored.landedAt)) record.landedAt = stored.landedAt;
  return record;
}

// How long one Play install-referrer read may take before it counts as a
// retryable failure. The Java plugin cannot bound this itself: a Play service
// that binds and then never calls back would leave the call pending forever.
export const INSTALL_REFERRER_TIMEOUT_MS = 10000;

// How many retryable failures an install tolerates before it stops asking, so
// a device with no Play Store does not call the API on every launch forever.
export const INSTALL_REFERRER_MAX_ATTEMPTS = 5;

const INSTALL_REFERRER_DONE = "done";

// InstallReferrerResponse codes that will not change on a retry:
// FEATURE_NOT_SUPPORTED (2) and DEVELOPER_ERROR (3). SERVICE_UNAVAILABLE (1),
// SERVICE_DISCONNECTED (-1), and anything else are worth another try on the
// next launch, which Google's 90-day referrer retention makes safe.
const TERMINAL_REFERRER_CODES = new Set(["2", "3"]);

// The install-referrer state, or `null` when localStorage cannot be read. A
// missing or unreadable value means no attempts have been made yet.
function readInstallReferrerState() {
  let raw;
  try {
    raw = window.localStorage.getItem(STORAGE_KEYS.INSTALL_REFERRER_STATE);
  } catch {
    return null;
  }
  if (raw === INSTALL_REFERRER_DONE) return { done: true, attempts: 0 };
  const attempts = typeof raw === "string" && /^\d+$/.test(raw) ? Number(raw) : 0;
  return { done: attempts >= INSTALL_REFERRER_MAX_ATTEMPTS, attempts };
}

function writeInstallReferrerState(value) {
  try {
    window.localStorage.setItem(STORAGE_KEYS.INSTALL_REFERRER_STATE, value);
  } catch {
    // Storage unavailable: the read is attempted again on the next launch.
  }
}

// The plugin rejects with the numeric response code as the message.
function isTerminalReferrerError(error) {
  return TERMINAL_REFERRER_CODES.has(String(error?.message ?? "").trim());
}

function recordRetryableFailure(attempts) {
  const next = attempts + 1;
  writeInstallReferrerState(
    next >= INSTALL_REFERRER_MAX_ATTEMPTS ? INSTALL_REFERRER_DONE : String(next)
  );
}

// Play reports its timestamps in whole seconds, and 0 means "not known".
// Returns null for anything the seed's landedAt check would drop.
function playSecondsToMs(seconds) {
  if (typeof seconds !== "number" || !(seconds > 0)) return null;
  const ms = seconds * 1000;
  return Number.isSafeInteger(ms) && isValidLandedAt(ms) ? ms : null;
}

// Store what Play answered and mark this install done. Play answered, so the
// install is done whatever the string held, including nothing at all, with one
// exception: a tagged referrer whose first touch did not make it into storage
// (a quota too full for the record but not for the short state value) counts
// as a retryable failure instead, so the next launch can try to store it again.
// The first touch is stored before the state says done, so a process killed
// in between retries rather than losing it.
function storeReferrerAnswer(details, attempts) {
  const now = Date.now();
  const record = parseUtmParams(details?.referrer, NaN, {
    allowForwardedLanding: true,
  });
  if (record) {
    // A forwarded al_landed the seed would drop anyway (below the 2020 floor,
    // or too large to be exact) must not hide a usable Play time.
    if (!(Number.isSafeInteger(record.landedAt) && isValidLandedAt(record.landedAt))) {
      record.landedAt =
        playSecondsToMs(details.referrerClickTimestampSeconds) ??
        playSecondsToMs(details.installBeginTimestampSeconds) ??
        now;
    }
    rememberFirstTouch(record, now);
    // Either this record or an earlier fresh first touch, which
    // rememberFirstTouch deliberately leaves in place, must now be stored.
    if (!isFresh(readStored(), now)) {
      recordRetryableFailure(attempts);
      return;
    }
  }
  writeInstallReferrerState(INSTALL_REFERRER_DONE);
}

/**
 * Android only: ask the Play Store once per install for the referrer string
 * the install came with, and remember its UTM values as this device's first
 * touch. Started fire-and-forget at boot by startAcquisitionCapture; nothing
 * awaits it, and the first-sign-in seed simply takes whatever has been stored
 * by the time it runs.
 *
 * landedAt is, in order of preference: the web landing time the Play badge
 * forwarded as `al_landed`; Google's campaign click time; the install-begin
 * time; the time of this read.
 *
 * The returned promise always resolves. Every failure is caught here, because
 * startAcquisitionCapture calls this outside its own try/catch and a rejection
 * must never reach main.js's startup path. Exported for its unit tests.
 */
export async function startAndroidInstallReferrer() {
  try {
    if (window.Capacitor?.getPlatform?.() !== "android") return;

    // `null` means storage is unavailable: try the read anyway, on every
    // launch, because a duplicate call to a local Play service costs less
    // than losing the attribution.
    const state = readInstallReferrerState();
    if (state?.done) return;
    const attempts = state ? state.attempts : 0;

    let registerPlugin;
    try {
      ({ registerPlugin } = await import("@capacitor/core"));
    } catch (error) {
      // Deterministic for this build, so asking again would never help.
      console.warn("Could not load Capacitor for the install referrer", error);
      writeInstallReferrerState(INSTALL_REFERRER_DONE);
      return;
    }

    // A synchronous throw from getReferrer rejects `read` like any other
    // failure. The timer is cleared as soon as `read` settles either way.
    let timer;
    const read = new Promise((resolve) =>
      resolve(registerPlugin("PlayInstallReferrer").getReferrer())
    );
    const timedOut = new Promise((resolve) => {
      timer = setTimeout(() => resolve(true), INSTALL_REFERRER_TIMEOUT_MS);
    });
    const outcome = read.then(
      (details) => ({ ok: true, details }),
      (error) => ({ ok: false, error })
    );
    const winner = await Promise.race([outcome, timedOut.then(() => null)]);
    clearTimeout(timer);

    if (winner === null) {
      // The ten seconds ran out, which is a retryable failure, counted now so
      // the five-attempt cap holds even if the process dies before Play
      // answers. An answer that still arrives later in this launch is handled
      // like an answer in time: stored if it is a referrer (which writes done
      // again after the record is stored), and final if it is a terminal code.
      // A late retryable rejection adds nothing, because the timeout already
      // counted this attempt.
      //
      // The cost of counting first: on the fifth attempt the state is done
      // before a late referrer is stored, so a process killed in that window
      // loses the referrer. The alternative, writing nothing until the late
      // answer, lets a Play service that never answers plus a short-lived
      // process make an unlimited number of attempts, and the cap wins.
      recordRetryableFailure(attempts);
      outcome
        .then((late) => {
          if (late.ok) {
            storeReferrerAnswer(late.details, attempts);
          } else if (isTerminalReferrerError(late.error)) {
            writeInstallReferrerState(INSTALL_REFERRER_DONE);
          }
        })
        .catch((error) => {
          console.warn("Could not store a late Play install referrer", error);
        });
      return;
    }

    if (!winner.ok) {
      if (isTerminalReferrerError(winner.error)) {
        writeInstallReferrerState(INSTALL_REFERRER_DONE);
      } else {
        recordRetryableFailure(attempts);
      }
      return;
    }

    storeReferrerAnswer(winner.details, attempts);
  } catch (error) {
    console.warn("Could not read the Play install referrer", error);
  }
}

/**
 * Run once at boot from main.js, before the router is installed, so the query
 * string is read before any navigation can replace the URL.
 */
export function startAcquisitionCapture() {
  try {
    const now = Date.now();
    rememberFirstTouch(parseUtmParams(window.location.search, now), now);
  } catch (error) {
    console.warn("Could not record the landing page's campaign tags", error);
  }
  startAndroidInstallReferrer();
}
